import { useRef, useEffect, useCallback, RefObject } from 'react';

/**
 * Hook that alerts when you click outside of the passed ref
 * @param onClick Function to call when a click is detected outside the referenced element
 * @returns React ref object to attach to the DOM element you want to monitor clicks outside of
 */
function useOutsideClick<T extends HTMLElement = HTMLDivElement>(onClick: () => void): RefObject<T> {
  const ref = useRef<T>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClick();
      }
    },
    [onClick]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  return ref;
}

export default useOutsideClick;
